import { useState, useCallback, useEffect, useRef } from 'react';
import { publicSurveyService } from '@/services/surveyService';
import type { SurveyDraft, DraftData, AnswerData } from '@/types';

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'offline';

export interface DraftManagerState {
  /** Current save status */
  saveStatus: DraftSaveStatus;
  /** Last saved timestamp */
  lastSavedAt: Date | null;
  /** Whether a draft exists for this survey */
  hasDraft: boolean;
  /** The loaded draft data (parsed) */
  draftData: DraftData | null;
  /** The raw draft from the server */
  draft: SurveyDraft | null;
  /** Whether draft is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
}

export interface DraftManagerActions {
  /** Mark that answers have changed (tracks unsaved state) */
  markAsChanged: () => void;
  /** Save the current answers as a draft (manual save) */
  saveManually: (answers: Record<number, AnswerData>, lastQuestionIndex: number, totalQuestions: number) => Promise<boolean>;
  /** Load an existing draft */
  loadDraft: () => Promise<DraftData | null>;
  /** Delete the current draft */
  deleteDraft: () => Promise<void>;
  /** Reset unsaved changes flag (e.g., after successful submit) */
  resetUnsavedChanges: () => void;
}

export interface DraftManager extends DraftManagerState, DraftManagerActions {}

/**
 * Hook to manage survey response drafts with MANUAL save functionality.
 *
 * This hook does NOT auto-save. It provides:
 * - Manual save via saveManually()
 * - Tracks unsaved changes via hasUnsavedChanges
 * - Shows save status for UI feedback
 * - Detects offline state
 * - Works with both authenticated and anonymous users
 *
 * @param surveyToken - The survey's public token or ID
 * @param anonymousToken - Anonymous respondent token (null for authenticated users)
 * @returns DraftManager object with state and actions
 */
export function useDraftManager(
  surveyToken: string,
  anonymousToken: string | null
): DraftManager {
  const [saveStatus, setSaveStatus] = useState<DraftSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftData, setDraftData] = useState<DraftData | null>(null);
  const [draft, setDraft] = useState<SurveyDraft | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isMountedRef = useRef(true);

  // Check online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update save status based on online status
  useEffect(() => {
    if (!isOnline && saveStatus !== 'offline') {
      setSaveStatus('offline');
    } else if (isOnline && saveStatus === 'offline') {
      setSaveStatus('idle');
    }
  }, [isOnline, saveStatus]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Mark that changes have been made (for unsaved indicator).
   */
  const markAsChanged = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  /**
   * Reset unsaved changes flag.
   */
  const resetUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  /**
   * Manual save function - saves immediately when called.
   * Returns true if save was successful, false otherwise.
   */
  const saveManually = useCallback(async (
    answers: Record<number, AnswerData>,
    lastQuestionIndex: number,
    totalQuestions: number
  ): Promise<boolean> => {
    // Don't save if no survey token
    if (!surveyToken) {
      return false;
    }

    if (!isOnline) {
      setSaveStatus('offline');
      return false;
    }

    setSaveStatus('saving');
    setError(null);

    try {
      const draftPayload: DraftData = {
        answers,
        lastQuestionIndex,
        totalQuestions,
        savedAt: new Date().toISOString(),
      };

      const response = await publicSurveyService.saveDraft(
        surveyToken,
        {
          responseDataJson: JSON.stringify(draftPayload),
          lastQuestionIndex,
        },
        anonymousToken ?? undefined
      );

      if (isMountedRef.current) {
        if (response.success && response.draft) {
          setDraft(response.draft);
          setDraftData(draftPayload);
          setHasDraft(true);
          setLastSavedAt(new Date());
          setSaveStatus('saved');
          setHasUnsavedChanges(false);

          // Reset to idle after 3 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setSaveStatus('idle');
            }
          }, 3000);

          return true;
        } else {
          throw new Error(response.message || 'Failed to save draft');
        }
      }
      return false;
    } catch (err) {
      // Detailed error logging for debugging
      const isAxiosError = err && typeof err === 'object' && 'response' in err;
      console.error('[DraftManager] Save failed:', {
        error: err,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
        errorStack: err instanceof Error ? err.stack : undefined,
        apiResponse: isAxiosError ? (err as { response?: unknown }).response : undefined,
        apiResponseData: isAxiosError ? (err as { response?: { data?: unknown } }).response?.data : undefined,
        apiResponseStatus: isAxiosError ? (err as { response?: { status?: number } }).response?.status : undefined,
        surveyToken,
        hasAnonymousToken: !!anonymousToken,
        anonymousTokenValue: anonymousToken,
        answersCount: Object.keys(answers).length,
        lastQuestionIndex,
        totalQuestions,
      });

      if (isMountedRef.current) {
        let errorMessage = 'Failed to save draft';
        if (isAxiosError) {
          const axiosErr = err as { response?: { data?: { message?: string } } };
          errorMessage = axiosErr.response?.data?.message || errorMessage;
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        setSaveStatus('error');

        // Reset to idle after 5 seconds
        setTimeout(() => {
          if (isMountedRef.current) {
            setSaveStatus('idle');
          }
        }, 5000);
      }
      return false;
    }
  }, [surveyToken, anonymousToken, isOnline]);

  /**
   * Load existing draft from server.
   */
  const loadDraft = useCallback(async (): Promise<DraftData | null> => {
    // Don't try to load if no survey token
    if (!surveyToken) {
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const serverDraft = await publicSurveyService.getDraft(
        surveyToken,
        anonymousToken ?? undefined
      );

      if (isMountedRef.current) {
        if (serverDraft) {
          setDraft(serverDraft);
          setHasDraft(true);

          // Parse the response data
          try {
            const parsed = JSON.parse(serverDraft.responseDataJson) as DraftData;
            setDraftData(parsed);
            setLastSavedAt(serverDraft.lastModifiedDate ? new Date(serverDraft.lastModifiedDate) : new Date(serverDraft.createdDate));
            return parsed;
          } catch {
            // Invalid JSON, treat as no draft
            setDraftData(null);
            setHasDraft(false);
          }
        } else {
          setDraft(null);
          setDraftData(null);
          setHasDraft(false);
        }
      }

      return null;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load draft');
        setHasDraft(false);
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [surveyToken, anonymousToken]);

  /**
   * Delete the current draft.
   */
  const deleteDraft = useCallback(async () => {
    if (!draft) return;

    try {
      await publicSurveyService.deleteDraft(
        surveyToken,
        draft.id,
        anonymousToken ?? undefined
      );

      if (isMountedRef.current) {
        setDraft(null);
        setDraftData(null);
        setHasDraft(false);
        setLastSavedAt(null);
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to delete draft');
      }
    }
  }, [surveyToken, anonymousToken, draft]);

  // Load draft on mount
  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  return {
    // State
    saveStatus,
    lastSavedAt,
    hasDraft,
    draftData,
    draft,
    isLoading,
    error,
    hasUnsavedChanges,

    // Actions
    markAsChanged,
    saveManually,
    loadDraft,
    deleteDraft,
    resetUnsavedChanges,
  };
}

export default useDraftManager;
